import React, { useLayoutEffect, useRef } from 'react';
import { usePeersona } from '../../contexts/peersona-context';
import { PeersonaMessageBubble } from './peersona-message-bubble';
import { PeersonaWelcomeHero } from './peersona-welcome-hero';

interface Props {
  scrollable?: boolean;
  className?: string;
  /** Click handler for the sticky "Learn more about {name}" strip. */
  onLearnMore?: () => void;
}

/** "Thinking…" pill, kept outside the bubble shell so it isn't logged as a real message. */
const StageRow: React.FC<{ message: string }> = ({ message }) => (
  <div className="flex justify-start pl-4" aria-live="polite">
    <div className="max-w-[85%] rounded-2xl bg-gray-50 px-4 py-2">
      <p className="font-open-sans text-sm italic leading-5 text-gray-500">{message}</p>
    </div>
  </div>
);

/**
 * Scrollable message log. Persistent welcome hero sits at the top of
 * the same scroll container so its sticky "Learn more about" strip
 * pins correctly once the rest of the hero scrolls past.
 */
export const PeersonaMessageArea: React.FC<Props> = ({ scrollable = true, className, onLearnMore }) => {
  const { messages, isSending, stage, chatbot, panelOpen } = usePeersona();
  const bottomRef = useRef<HTMLDivElement>(null);
  const peerName = chatbot?.display_name || chatbot?.name || 'Peer';
  const avatar = chatbot?.avatar_file_path ?? null;
  const scopeLine = (chatbot?.scope_line ?? '').trim();

  useLayoutEffect(() => {
    if (!panelOpen) return;
    bottomRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
  }, [messages, isSending, stage?.message, panelOpen]);

  const tail = messages[messages.length - 1];
  const showStageBelowList = isSending && stage?.message && (!tail || tail.role !== 'assistant' || !tail.streaming);

  return (
    <div
      className={['min-h-0 flex-1', scrollable ? 'overflow-y-auto' : '', className ?? ''].filter(Boolean).join(' ')}
      style={{ backgroundColor: 'transparent' }}
    >
      <PeersonaWelcomeHero
        peerName={peerName}
        avatar={avatar}
        scopeLine={scopeLine || null}
        welcomeVideoVimeoId={chatbot?.welcome_video_vimeo_id ?? null}
        onLearnMore={onLearnMore}
      />
      {messages.length > 0 || showStageBelowList ? (
        <div className="space-y-3 pt-6">
          {messages.map((m, i) => (
            <PeersonaMessageBubble
              key={`${i}-${m.timestamp ?? ''}`}
              message={m}
              peerName={peerName}
              peerAvatar={avatar}
            />
          ))}
          {showStageBelowList ? <StageRow message={stage!.message} /> : null}
        </div>
      ) : null}
      <div ref={bottomRef} />
    </div>
  );
};
